import { useState, useEffect } from 'react';
import { logEvent, analytics } from '../firebase.js';
import { AIService } from '../services/ai-service.js';
import { BillingService } from '../services/billing-service.js';
import { useLocale } from '../hooks/useLocale.jsx';
import { useCreditConfig } from '../services/credit-config.js';
import { usePaintMask, BRUSH_MIN, BRUSH_MAX } from '../hooks/usePaintMask.js';

const MAX_FIELD = 200;

export function EditRegionModal({ design, photoIndex, user, initialReplacement = '', onClose, onPendingStart, onPendingDone, onPendingError, onOutOfCredits }) {
  const { t, lang } = useLocale();
  const creditCfg = useCreditConfig();
  const [replacement, setReplacement] = useState(initialReplacement);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState('free');

  useEffect(() => {
    if (!user?.uid || user.isAnonymous) { setPlan('free'); return; }
    return BillingService.subscribeToSubscription(user.uid, (sub) => setPlan(sub?.plan || 'free'));
  }, [user?.uid, user?.isAnonymous]);

  const baseSourceUrl = design.generatedImageUrls?.[photoIndex];
  // SW 가 같은 URL 을 opaque (no-cors) 로 캐싱해뒀다면 우리의 crossOrigin="anonymous"
  // (CORS) 요청에 cached opaque 반환 → fail. 1차 실패 시 cache-bust query param 으로
  // SW 캐시 우회해서 fresh fetch. SW config 도 별도로 statuses [200] 로 제한해
  // 새로 들어오는 opaque 응답은 더 이상 캐싱 안 함.
  const [retry, setRetry] = useState(0);
  const sourceUrl = retry > 0 && baseSourceUrl
    ? `${baseSourceUrl}${baseSourceUrl.includes('?') ? '&' : '?'}cb=${retry}`
    : baseSourceUrl;

  const { imgRef, canvasRef, hasMask, brushSize, setBrushSize, undoStroke, clearMask, composeOverlayBlob, handlers } = usePaintMask({ src: sourceUrl });

  const handleSubmit = async () => {
    setError(null);
    // 로그인 사용자는 칠하지 않고 말로만도 된다 ("현관 옆에 동상 추가" 처럼 더하는
    // 요청). 칠한 영역이 있으면 그 자리만. 구버전 동기 경로는 영역이 필수.
    const signedIn = user && !user.isAnonymous;
    if (!hasMask && !signedIn) { setError(t('editRegionMaskRequired')); return; }
    if (!replacement.trim()) { setError(t('editRegionReplacementRequired')); return; }

    setSubmitting(true);
    let overlayBlob = null;
    try {
      if (hasMask) overlayBlob = await composeOverlayBlob();
    } catch (err) {
      console.error('Overlay compose failed:', err);
      setError(t('errEditGeneric'));
      setSubmitting(false);
      return;
    }

    // Hand off to App-level pending banner so the user can keep browsing
    // while the edit runs. Modal closes immediately — same UX as the main
    // Generate flow's PendingBanner.
    onPendingStart?.();
    onClose();

    const replacementText = replacement.trim();
    (async () => {
      try {
        let newDesignId;
        if (signedIn) {
          // 2.3.0 — 서버 작업. 칠한 사본만 올리고 맡긴다. 앱을 닫아도 서버가
          // 끝까지 만들어 저장하고 푸시로 알린다 (예전 경로는 앱이 결과를 받아
          // 저장했기 때문에 폰을 덮으면 요청이 끊겨 "실패" 로 끝났다).
          const overlayUrl = overlayBlob ? await AIService.uploadEditOverlay(overlayBlob) : null;
          const { designId } = await AIService.startDesignJob({
            type: 'edit',
            sourceDesignId: design.id,
            photoIndex,
            replacement: replacementText,
            ...(overlayUrl && { overlayUrl }),
            lang,
          });
          const done = await AIService.waitForDesign(designId);
          if (done.status !== 'success') throw Object.assign(new Error('EDIT_FAILED'), { code: 'EDIT_FAILED' });
          newDesignId = designId;
        } else {
          const result = await AIService.editDesignRegion({
            sourceDesignId: design.id,
            photoIndex,
            replacement: replacementText,
            overlayBlob,
          });
          const generatedUrl = await AIService.uploadDesignImage(
            result.generatedImage.data,
            result.generatedImage.mimeType,
          );
          const sourceOriginal = design.originalImageUrls?.[photoIndex] || null;
          const savedId = await AIService.saveDesign(
            {
              spaceAnalysis: design.spaceAnalysis,
              styleDescription: design.styleDescription,
              colorPalette: design.colorPalette,
              furnitureRecommendations: design.furnitureRecommendations,
              decorativeElements: design.decorativeElements,
              lightingPlan: design.lightingPlan,
              estimatedCost: design.estimatedCost,
              tips: design.tips,
              generatedImageUrls: [generatedUrl],
            },
            sourceOriginal ? [sourceOriginal] : [],
            design.style,
            design.customStyleInput || null,
            `Edit: ${replacementText}`,
            design.lang || lang,
            design.budget || 'mid',
            design.mode || 'redesign',
            design.id,
            design.spaceCategory || 'interior',
            design.exteriorOptions || null,
            design.referenceImageUrl || null,
            design.outputType || null,
          );
          newDesignId = savedId;
        }
        logEvent(analytics, 'design_edited', { source: design.id });
        onPendingDone?.(newDesignId);
      } catch (err) {
        console.error('Edit failed:', err);
        logEvent(analytics, 'design_edit_failed', { code: err.code || 'unknown' });
        if (err.code === 'INSUFFICIENT_CREDITS' || err.status === 402) {
          logEvent(analytics, 'credit_exhausted', { surface: 'edit' });
          // 생성·Gio 와 같이 크레딧 모달을 띄운다 — 앱이면 팩 구매가 거기 있다.
          onOutOfCredits?.();
        }
        const msg = err.code === 'INSUFFICIENT_CREDITS' || err.status === 402
          ? (user && !user.isAnonymous
            ? t('errOutOfCreditsToday', { n: Math.floor(creditCfg.daily / creditCfg.price.generate) })
            : t('errInsufficientCredits'))
          : err.code === 'INVALID_CUSTOM_COMMAND'
          ? t('errBlockedCustomCommand')
          : t('errEditGeneric');
        onPendingError?.(msg);
      }
    })();
  };

  return (
    <div className="modal-overlay" onClick={() => !submitting && onClose()}>
      <div className="modal-card edit-region-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} disabled={submitting} aria-label="Close">
          <i className="material-symbols-rounded">close</i>
        </button>
        <h3>{t('editRegionTitle')}</h3>
        <p className="modal-desc">{t('editRegionBrushHint')}</p>

        <div className="edit-region-canvas-wrap">
          <img
            ref={imgRef}
            src={sourceUrl}
            alt=""
            className="edit-region-image"
            crossOrigin="anonymous"
            onError={() => {
              // Cache mismatch / 일시적 fetch fail 대비 1회 cache-bust 재시도.
              // 두 번 실패하면 사용자에게 명확히 알림 (state error).
              if (retry < 2) setRetry(r => r + 1);
              else setError(t('errEditGeneric'));
            }}
          />
          <canvas
            ref={canvasRef}
            className="edit-region-canvas"
            {...handlers}
          />
        </div>

        <div className="edit-region-tools">
          <label className="edit-tool-size">
            <span className="edit-tool-size-label">{t('editRegionSize')}</span>
            {/* 채워진 구간을 잉크로 그리기 위해 현재 값을 CSS 변수로 넘긴다.
                크롬엔 ::-webkit-slider-progress 가 없어서 트랙 배경을
                gradient 로 직접 칠하는 방법밖에 없다. */}
            <input
              type="range"
              min={BRUSH_MIN}
              max={BRUSH_MAX}
              step="2"
              value={brushSize}
              style={{ '--range-pct': `${((brushSize - BRUSH_MIN) / (BRUSH_MAX - BRUSH_MIN)) * 100}%` }}
              onChange={e => setBrushSize(Number(e.target.value))}
              disabled={submitting}
            />
          </label>
          <button
            type="button"
            className="edit-tool-btn"
            onClick={undoStroke}
            disabled={submitting || !hasMask}
          >
            <i className="material-symbols-rounded">undo</i>
            <span>{t('editRegionUndo')}</span>
          </button>
          <button
            type="button"
            className="edit-tool-btn edit-tool-clear"
            onClick={clearMask}
            disabled={submitting || !hasMask}
          >
            <i className="material-symbols-rounded">restart_alt</i>
            <span>{t('editRegionClear')}</span>
          </button>
        </div>

        <label className="edit-field-label">{t('editRegionReplacementLabel')}</label>
        <input
          className="edit-field-input"
          value={replacement}
          onChange={e => setReplacement(e.target.value.slice(0, MAX_FIELD))}
          placeholder={t('editRegionReplacementPlaceholder')}
          maxLength={MAX_FIELD}
          disabled={submitting}
        />

        {plan !== 'pro' && (
          <p className="modal-meta">{t('editRegionMeta', { n: creditCfg.price.edit })}</p>
        )}

        {error && <p className="edit-region-error">{error}</p>}

        <div className="controls" style={{ marginTop: '1rem' }}>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !replacement.trim() || (!hasMask && !(user && !user.isAnonymous))}
          >
            {t('editRegionSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
